/*
 * Copyright (C) 2021 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Pipe, PipeTransform } from '@angular/core';
import moment from 'moment';

/**
 * Takes in UNIX format timestamp and returns a relative message by comparison to now
 *
 * @example
 * // Default format (moment.js fromNow)
 * timestamp | fromNow
 * // Output: "8 days ago" or "in 2 days"
 *
 * @example
 * // Precise format with days and hours
 * timestamp | fromNow:true
 * // Output: "156 days, 5 hours" or "2 days, 1 hour"
 *
 * @example
 * // Include suffix with precise format
 * timestamp | fromNow:true:true
 * // Output: "156 days, 5 hours from now" or "2 days, 1 hour ago"
 */
@Pipe({
  name: 'fromNow',
  standalone: true,
  pure: false
})
export class FromNowPipe implements PipeTransform {

  transform(timestamp: number | string | Date, precise = false, includeSuffix = false): string {
    if (!precise) {
      return moment(timestamp).fromNow();
    }

    const now = moment();
    const target = moment(timestamp);
    const isPast = target.isBefore(now);
    const duration = moment.duration(isPast ? now.diff(target) : target.diff(now));

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();

    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
    }

    if (hours > 0) {
      parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }

    // Only show minutes if less than 1 day
    if (days === 0 && minutes > 0) {
      parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }

    if (parts.length === 0) {
      return 'less than a minute' + (includeSuffix ? (isPast ? ' ago' : ' from now') : '');
    }

    const timeString = parts.join(', ');

    if (includeSuffix) {
      return isPast ? `${timeString} ago` : `${timeString} from now`;
    }

    return timeString;
  }
}
